import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { Collapsible, CollapsiblePanel, CollapsibleTrigger } from '../../../web/components-v2/Collapsible';
import { THEME } from '../../../web/components-v2/Theme/theme';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';

const { colors, spacing, typography } = THEME;

interface IProps {
	title: string;
	/** Rendered before the trigger so a control (e.g. a group checkbox) is not nested inside its button. */
	leadingControl?: React.ReactNode;
	statusChip?: React.ReactNode;
	defaultOpen?: boolean;
	children: React.ReactNode;
}

export function CollapsibleProviderSection({
	title,
	leadingControl,
	statusChip,
	defaultOpen = false,
	children,
}: IProps) {
	const [isOpen, setIsOpen] = React.useState(defaultOpen);

	React.useEffect(() => setIsOpen(defaultOpen), [defaultOpen]);

	return (
		<Collapsible open={isOpen} onOpenChange={setIsOpen}>
			<div className={css(styles.container)}>
				{/* The chip sits outside the trigger: it renders a div, and the trigger renders a button. */}
				<div className={css(styles.header)}>
					{leadingControl}
					<CollapsibleTrigger className={css(styles.trigger)}>
						<ChevronDown className={css(styles.chevron, isOpen && styles.chevronOpen)} width={16} height={16} />
						<span className={css(styles.title)}>{title}</span>
					</CollapsibleTrigger>
					{statusChip}
				</div>
				<CollapsiblePanel>
					<div className={css(styles.content)}>{children}</div>
				</CollapsiblePanel>
			</div>
		</Collapsible>
	);
}

const styles = StyleSheet.create({
	container: {
		borderBottom: THEME.common.border,
		':last-child': {
			borderBottom: 'none',
		},
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		padding: `${spacing.sm}px ${spacing.md}px`,
	},
	trigger: {
		display: 'flex',
		alignItems: 'center',
		gap: spacing.sm,
		flexGrow: 1,
		padding: 0,
		border: 'none',
		backgroundColor: 'transparent',
		cursor: 'pointer',
		textAlign: 'left' as const,
	},
	title: {
		fontSize: typography.fontSizes.sm,
		fontWeight: typography.fontWeights.bold,
		color: colors.text.main,
		flexGrow: 1,
	},
	chevron: {
		color: colors.text.light,
		transition: 'transform 150ms ease-out',
		flexShrink: 0,
	},
	chevronOpen: {
		transform: 'rotate(180deg)',
	},
	content: {
		padding: `0 ${spacing.md}px ${spacing.sm}px`,
	},
});
